import {
  AssignmentTurnedInOutlined as AssignmentIcon,
  BookOutlined as BookIcon,
  HomeOutlined as HomeIcon,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { logoutUser } from "../../../redux/Actions/authActions";
import NotificationMenu from "../../NotificationsMenu/NotificationsMenu";
import LanguageLabel from "../../LanguageToggle/LanguageLabel";

const StudentMobileNav = () => {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const studentId = useSelector((state) => state.auth.user.studentId);
  const profileName = useSelector((state) => state.auth.user?.profileName);

  const [open, setOpen] = useState(false);

  const isDuringPlacementTest = location.pathname.startsWith("/placement/test");
  const dir = i18n?.language === "ar" ? "rtl" : "ltr";
  const anchor = dir === "rtl" ? "right" : "left";

  const items = useMemo(
    () => [
      {
        key: "home",
        to: "/",
        icon: <HomeIcon />,
        label: { en: "Home", ar: "الرئيسية" },
      },
      {
        key: "enrolled-courses",
        to: "/dashboard",
        icon: <BookIcon />,
        label: { en: "Enrolled courses", de: "Meine Kurse", ar: "الدورات المسجل بها" },
      },
      {
        key: "submissions",
        to: "/dashboard/student/submissions",
        icon: <AssignmentIcon />,
        label: { en: "Submissions", de: "Bewerbungen", ar: "التسليمات" },
      },
    ],
    []
  );

  const currentLanguage = i18n.language?.split("-")[0];
  const selectedLanguage = ["ar", "de", "en"].includes(currentLanguage)
    ? currentLanguage
    : "en";
  const labelFor = (item) => item.label[selectedLanguage] || item.label.en;

  const handleLogout = () => {
    dispatch(logoutUser(false));
    setOpen(false);
  };

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "#FFFFFF",
          boxShadow: "none",
          border: "1px solid #D2D6DB",
          zIndex: 1300,
          height: 72,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Box
            component={Link}
            to="/"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Box
              component="img"
              src="/al-germanist-logo.png"
              alt="ALGermanist"
                sx={{ height: 70, width: 92, mt: 0.5 }}
              />
          </Box>

          <IconButton
            color="black"
            edge="end"
            aria-label="open drawer"
            onClick={() => !isDuringPlacementTest && setOpen(true)}
            disabled={isDuringPlacementTest}
          >
            <MenuIcon sx={{ fontSize: { xs: 22, sm: 24, md: 24 } }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor={anchor}
        open={open}
        sx={{ zIndex: 1600 }}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: "70vw",
            maxWidth: 320,
            bgcolor: "#fff",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", p: 2, gap: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {profileName}
            </Typography>
          </Box>
          <NotificationMenu studentId={studentId} />
          <IconButton onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <List sx={{ p: 0 }}>
          {items.map((item) => {
            const active = item.to && location.pathname === item.to;
            const content = (
              <ListItemButton
                selected={!!active}
                sx={{
                  px: 2,
                  py: 1,
                  minHeight: 44,
                  "&.Mui-selected": { bgcolor: "#eef3f3" },
                  justifyContent: "flex-start",
                }}
                onClick={() => setOpen(false)}
              >
                <ListItemText
                  primary={labelFor(item)}
                  primaryTypographyProps={{
                    sx: { fontWeight: 600, textAlign: "start" }, // Center the text
                  }}
                />
              </ListItemButton>
            );

            return (
              <ListItem key={item.key} disablePadding>
                {item.to ? (
                  <Link
                    to={item.to}
                    onClick={() => setOpen(false)}
                    style={{
                      textDecoration: "none",
                      color: "inherit",
                      width: "100%",
                    }}
                  >
                    {content}
                  </Link>
                ) : (
                  content
                )}
              </ListItem>
            );
          })}
        </List>
        <Divider />

        <Box sx={{ p: 2 }}>
          {/* Language Toggle as Buttons */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "nowrap",
              width: "100%",
            }}
          >
            <Button
              variant={selectedLanguage === "en" ? "contained" : "outlined"}
              size="small"
              onClick={() => handleLanguageChange("en")}
              sx={{
                minWidth: { xs: 68, sm: 80 },
                borderRadius: "18px",
                fontWeight: 600,
                fontSize: { xs: 12, sm: 13 },
                px: { xs: 1.25, sm: 1.5 },
                backgroundColor: selectedLanguage === "en" ? "#F6F7F8" : "white",
                color: "#333",
                borderColor: "#F6F7F8",
                "&:hover": {
                  backgroundColor: "#e0e0e0",
                },
              }}
            >
              <LanguageLabel language="en" full />
            </Button>
            <Button
              variant={selectedLanguage === "de" ? "contained" : "outlined"}
              size="small"
              onClick={() => handleLanguageChange("de")}
              sx={{
                minWidth: { xs: 68, sm: 80 },
                borderRadius: "18px",
                fontWeight: 600,
                fontSize: { xs: 12, sm: 13 },
                px: { xs: 1.25, sm: 1.5 },
                backgroundColor: selectedLanguage === "de" ? "#F6F7F8" : "white",
                color: "#333",
                borderColor: "#F6F7F8",
                "&:hover": {
                  backgroundColor: "#e0e0e0",
                },
              }}
            >
              <LanguageLabel language="de" full />
            </Button>
            <Button
              variant={selectedLanguage === "ar" ? "contained" : "outlined"}
              size="small"
              onClick={() => handleLanguageChange("ar")}
              sx={{
                minWidth: { xs: 68, sm: 80 },
                borderRadius: "18px",
                fontWeight: 600,
                fontSize: { xs: 12, sm: 13 },
                px: { xs: 1.25, sm: 1.5 },
                backgroundColor: selectedLanguage === "ar" ? "#F6F7F8" : "white",
                color: "#333",
                borderColor: "#F6F7F8",
                "&:hover": {
                  backgroundColor: "#e0e0e0",
                },
              }}
            >
              <LanguageLabel language="ar" full />
            </Button>
          </Box>
        </Box>
        <Divider />
        <Box sx={{ flexGrow: 1 }} />

        <Divider />

        <List>
          <ListItem disablePadding>
            <Link
              to="/student/profile"
              onClick={() => setOpen(false)}
              style={{
                textDecoration: "none",
                color: "inherit",
                width: "100%",
              }}
            >
              <ListItemButton>
                <ListItemText
                  primary={t("navbar.profile")}
                  primaryTypographyProps={{ sx: { textAlign: "center" } }}
                />
              </ListItemButton>
            </Link>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout}>
              <ListItemText
                primary={t("navbar.logout")}
                primaryTypographyProps={{ sx: { textAlign: "center" } }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
};

export default StudentMobileNav;
